import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import remove from 'lodash/remove';
import { Button, Table } from 'semantic-ui-react';
import cx from 'classnames';
import { defineMessages, useIntl } from 'react-intl';

import Cell from '@plone/volto-slate/blocks/Table/Cell';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import { BlockDataForm } from '@plone/volto/components/manage/Form';
import TableSchema from '@plone/volto-slate/blocks/Table/schema';

import rowBeforeSVG from '@plone/volto/icons/row-before.svg';
import rowAfterSVG from '@plone/volto/icons/row-after.svg';
import colBeforeSVG from '@plone/volto/icons/column-before.svg';
import colAfterSVG from '@plone/volto/icons/column-after.svg';
import rowDeleteSVG from '@plone/volto/icons/row-delete.svg';
import colDeleteSVG from '@plone/volto/icons/column-delete.svg';

const getId = () => Math.floor(Math.random() * Math.pow(2, 24)).toString(32);

const getEmptyParagraph = () => [{ type: 'p', children: [{ text: '' }] }];

const emptyCell = (type = 'data') => ({
  key: getId(),
  type,
  value: getEmptyParagraph(),
});

const emptyRow = (cells) => ({
  key: getId(),
  cells: map(cells, () => emptyCell()),
});

const initialTable = {
  hideHeaders: false,
  fixed: true,
  compact: false,
  basic: false,
  celled: true,
  inverted: false,
  striped: false,
  rows: [
    {
      key: getId(),
      cells: [emptyCell('header'), emptyCell('header')],
    },
    {
      key: getId(),
      cells: [emptyCell(), emptyCell()],
    },
  ],
};

const messages = defineMessages({
  insertRowBefore: {
    id: 'Insert row before',
    defaultMessage: 'Insert row before',
  },
  insertRowAfter: {
    id: 'Insert row after',
    defaultMessage: 'Insert row after',
  },
  deleteRow: { id: 'Delete row', defaultMessage: 'Delete row' },
  insertColBefore: {
    id: 'Insert col before',
    defaultMessage: 'Insert col before',
  },
  insertColAfter: {
    id: 'Insert col after',
    defaultMessage: 'Insert col after',
  },
  deleteCol: { id: 'Delete col', defaultMessage: 'Delete col' },
});

function Edit(props) {
  const {
    data,
    block,
    selected,
    index,
    onChangeBlock,
    onAddBlock,
    onSelectBlock,
    blocksConfig,
  } = props;

  const intl = useIntl();

  const [selectedCell, setSelectedCell] = useState({ row: 0, cell: 0 });
  const [isClient, setIsClient] = useState(false);

  // Init table (ex componentDidMount + componentWillReceiveProps)
  useEffect(() => {
    if (!data.table || isEmpty(data.table)) {
      onChangeBlock(block, {
        ...data,
        table: initialTable,
      });
    }
  }, [data, block, onChangeBlock]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!selected) {
      setSelectedCell(null);
    }
  }, [selected]);

  const onChangeCell = useCallback(
    (row, cell, slateValue) => {
      const table = JSON.parse(JSON.stringify(data.table));
      table.rows[row].cells[cell].value = slateValue;

      onChangeBlock(block, {
        ...data,
        table,
      });
    },
    [data, block, onChangeBlock],
  );

  const onInsertRowBefore = () => {
    const table = data.table;

    onChangeBlock(block, {
      ...data,
      table: {
        ...table,
        rows: [
          ...table.rows.slice(0, selectedCell.row),
          emptyRow(table.rows[0].cells),
          ...table.rows.slice(selectedCell.row),
        ],
      },
    });

    setSelectedCell((s) => ({ ...s, row: s.row + 1 }));
  };

  const onInsertRowAfter = () => {
    const table = data.table;

    onChangeBlock(block, {
      ...data,
      table: {
        ...table,
        rows: [
          ...table.rows.slice(0, selectedCell.row + 1),
          emptyRow(table.rows[0].cells),
          ...table.rows.slice(selectedCell.row + 1),
        ],
      },
    });
  };

  const onInsertColBefore = () => {
    const table = data.table;

    onChangeBlock(block, {
      ...data,
      table: {
        ...table,
        rows: map(table.rows, (row, index) => ({
          ...row,
          cells: [
            ...row.cells.slice(0, selectedCell.cell),
            emptyCell(table.rows[index].cells[selectedCell.cell].type),
            ...row.cells.slice(selectedCell.cell),
          ],
        })),
      },
    });

    setSelectedCell((s) => ({ ...s, cell: s.cell + 1 }));
  };

  const onInsertColAfter = () => {
    const table = data.table;

    onChangeBlock(block, {
      ...data,
      table: {
        ...table,
        rows: map(table.rows, (row, index) => ({
          ...row,
          cells: [
            ...row.cells.slice(0, selectedCell.cell + 1),
            emptyCell(table.rows[index].cells[selectedCell.cell].type),
            ...row.cells.slice(selectedCell.cell + 1),
          ],
        })),
      },
    });
  };

  const onDeleteRow = () => {
    const table = data.table;

    onChangeBlock(block, {
      ...data,
      table: {
        ...table,
        rows: remove(table.rows, (_, i) => i !== selectedCell.row),
      },
    });
  };

  const onDeleteCol = () => {
    const table = data.table;

    onChangeBlock(block, {
      ...data,
      table: {
        ...table,
        rows: map(table.rows, (row) => ({
          ...row,
          cells: remove(row.cells, (_, i) => i !== selectedCell.cell),
        })),
      },
    });
  };

  const headers = data.table?.rows?.[0]?.cells || [];
  const rows = data.table?.rows?.slice(1) || [];
  const schema = TableSchema(props);

  return (
    <div className={cx('block table', { selected })}>
      {selected && (
        <div className="toolbar">
          <Button icon basic onClick={onInsertRowBefore}>
            <Icon name={rowBeforeSVG} size="24px" />
          </Button>
          <Button icon basic onClick={onInsertRowAfter}>
            <Icon name={rowAfterSVG} size="24px" />
          </Button>
          <Button icon basic onClick={onDeleteRow}>
            <Icon name={rowDeleteSVG} size="24px" />
          </Button>
          <Button icon basic onClick={onInsertColBefore}>
            <Icon name={colBeforeSVG} size="24px" />
          </Button>
          <Button icon basic onClick={onInsertColAfter}>
            <Icon name={colAfterSVG} size="24px" />
          </Button>
          <Button icon basic onClick={onDeleteCol}>
            <Icon name={colDeleteSVG} size="24px" />
          </Button>
        </div>
      )}

      {data.table && (
        <div className="ui container">
          <div className="table-container">
            <Table
              fixed={data.table.fixed}
              compact={data.table.compact}
              basic={data.table.basic ? 'very' : false}
              celled={data.table.celled}
              inverted={data.table.inverted}
              striped={data.table.striped}
              className="slate-table-block"
              unstackable
            >
              {!data.table.hideHeaders && (
                <Table.Header>
                  <Table.Row textAlign="left">
                    {headers.map((cell, cellIndex) => (
                      <Table.HeaderCell
                        key={cell.key}
                        textAlign="left"
                        verticalAlign="middle"
                      >
                        <Cell
                          value={cell.value}
                          row={0}
                          cell={cellIndex}
                          onSelectCell={(row, cell) =>
                            setSelectedCell({ row, cell })
                          }
                          selected={
                            selected &&
                            selectedCell &&
                            selectedCell.row === 0 &&
                            selectedCell.cell === cellIndex
                          }
                          selectedCell={selectedCell}
                          isTableBlockSelected={selected}
                          onAddBlock={onAddBlock}
                          onSelectBlock={onSelectBlock}
                          onChange={onChangeCell}
                          index={index}
                        />
                      </Table.HeaderCell>
                    ))}
                  </Table.Row>
                </Table.Header>
              )}

              <Table.Body>
                {rows.map((row, rowIndex) => (
                  <Table.Row key={row.key}>
                    {row.cells.map((cell, cellIndex) => (
                      <Table.Cell
                        key={cell.key}
                        textAlign="left"
                        verticalAlign="middle"
                        className={
                          selected &&
                          selectedCell &&
                          selectedCell.row === rowIndex + 1 &&
                          selectedCell.cell === cellIndex
                            ? 'selected'
                            : ''
                        }
                      >
                        <Cell
                          value={cell.value}
                          row={rowIndex + 1}
                          cell={cellIndex}
                          onSelectCell={(row, cell) =>
                            setSelectedCell({ row, cell })
                          }
                          selected={
                            selected &&
                            selectedCell &&
                            selectedCell.row === rowIndex + 1 &&
                            selectedCell.cell === cellIndex
                          }
                          selectedCell={selectedCell}
                          isTableBlockSelected={selected}
                          onAddBlock={onAddBlock}
                          onSelectBlock={onSelectBlock}
                          onChange={onChangeCell}
                          index={index}
                        />
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </div>
      )}

      {selected && selectedCell && isClient && (
        <SidebarPortal selected={selected}>
          <BlockDataForm
            schema={schema}
            title={schema.title}
            formData={data}
            block={block}
            blocksConfig={blocksConfig}
            onChangeBlock={onChangeBlock}
            onChangeField={(id, value) =>
              onChangeBlock(block, { ...data, [id]: value })
            }
          />
        </SidebarPortal>
      )}
    </div>
  );
}

Edit.propTypes = {
  data: PropTypes.object.isRequired,
  block: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  selected: PropTypes.bool.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  onAddBlock: PropTypes.func.isRequired,
  onSelectBlock: PropTypes.func.isRequired,
};

export default Edit;
